import { useCallback, useState } from 'react';
import type { JSX } from 'react';

import { PasswordEntry } from './PasswordEntry';
import type { PasswordEntryProps } from './PasswordEntry';
import { HamburgerMenu } from './HamburgerMenu';

export function LandingPage(props: PasswordEntryProps): JSX.Element {
  // FAQのアコーディオン開閉状態を管理
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = useCallback((index: number) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  }, []);

  const faqData = [
    {
      q: '本当に無料ですか？登録はいりませんか？',
      a: 'はい、完全無料で会員登録も一切不要です。メールアドレスや電話番号、LINEやSNSアカウントの連携なども必要ありません。ブラウザだけで即時にご利用いただけます。',
    },
    {
      q: '合言葉はどうやって決めればいいですか？',
      a: 'お互いしか知らない言葉（例: 二人共通の思い出、好きな食べ物、約束の数字など）を自由に決めてください。相手にもその「合言葉」とあなたの「ニックネーム」を共有するだけで、すぐに同じチャットルームに入室できます。',
    },
    {
      q: '会話の履歴は残りますか？消すことはできますか？',
      a: '会話履歴は同じ合言葉を使う限り保存され、いつでも読み返せます。「合言葉だけを後から変更する」機能はありませんが、別の合言葉で始めれば新しい部屋になり、前の部屋には戻らずにやり取りをリセットできます。ただし前の部屋と履歴はサーバー上に残るため、その合言葉を知っている相手は引き続き閲覧できます。履歴を完全に消したい場合は、ログインして部屋を作成したオーナーがマイページから「部屋を削除」してください（ゲスト利用では削除できません）。',
    },
    {
      q: '3人以上が入ってくることはありませんか？',
      a: 'はい。ChocoMesseは完全に「二人専用」で、新しいニックネームの3人目が同じ合言葉で入ろうとしても拒否（reject）されます。ただし、合言葉と参加者のニックネームの両方を知っている人は、その参加者として入室できてしまいます。合言葉とニックネームはお互いだけの秘密にしてください。',
    },
  ];

  return (
    <div className="theme-stage">
      {/* ページ全体の右上に固定するハンバーガーメニュー */}
      <div className="lp-menu">
        <HamburgerMenu />
      </div>

      {/* 装飾用の背景 blob (dusty-pastel テーマ) */}
      <span className="theme-blob theme-blob--a" aria-hidden="true" />
      <span className="theme-blob theme-blob--b" aria-hidden="true" />
      <span className="theme-blob theme-blob--c" aria-hidden="true" />

      <div className="theme-stage__content">
        <div className="lp-container">
          {/* ヒーローセクション */}
          <section className="lp-hero" aria-label="ヒミツのチャット紹介">
            <img
              src="/images/logo_nbg.png"
              alt="ChocoMesse"
              className="lp-hero__logo"
              width={280}
              height={93}
              decoding="async"
            />
            <p className="lp-hero__tagline">ちょこっとメッセージ、秘密に届ける。</p>
            <div className="lp-hero__badge">
              <span aria-hidden="true">🔒</span>
              <span>完全匿名・二人だけの暗黙のチャット</span>
            </div>

            <h1 className="lp-hero__title">
              合言葉でつながる<br />
              <span>二人の秘密の世界</span>
            </h1>

            <p className="lp-hero__description">
              個人情報ゼロ、会員登録も一切不要。<br />
              お互いに決めた「合言葉」を入力するだけで、<br />
              今すぐ二人だけのプライベートトークを始められます。
            </p>

            {/* 入室フォームを TOP のヒーロー内に一体化（全画面ラッパーなしの埋め込み表示） */}
            <PasswordEntry {...props} showMenu={false} embedded />
          </section>

          {/* 悩み共感セクション */}
          <section className="lp-section lp-section--light" aria-label="こんなお悩みありませんか？">
            <div className="lp-section-inner">
              <h2 className="lp-section__title">こんなお悩みありませんか？</h2>
              <p className="lp-section__subtitle">個人情報を明かさず、安全につながれるチャットです</p>

              <div className="lp-troubles">
                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">💬</span>
                  <h3 className="lp-trouble-card__title">プライベートのLINEを教えたくない</h3>
                  <p className="lp-trouble-card__text">
                    本アカを聞かれて断りづらい時も、「このシークレットチャットで話そう！」と伝えるだけで、個人の連絡先を明かさずにやり取りできます。
                  </p>
                </div>

                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">🔍</span>
                  <h3 className="lp-trouble-card__title">本名やSNSがバレるのが怖い</h3>
                  <p className="lp-trouble-card__text">
                    LINE IDや電話番号からTwitter、Instagramなどの個人アカウントを特定される「身バレ」や、本名の流出を防ぎます。
                  </p>
                </div>

                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">🚫</span>
                  <h3 className="lp-trouble-card__title">ブロック後もしつこくされる</h3>
                  <p className="lp-trouble-card__text">
                    LINEのようにブロックしても、アカウント自体が残ることはありません。連絡を断ちたい時は、その部屋に戻らないだけでOK。さらにログインして部屋を管理していれば、ブロックや部屋の削除でしっかり関係を断つこともできます。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 3つの特徴セクション */}
          <section className="lp-section" aria-label="ChocoMesseが選ばれる理由">
            <h2 className="lp-section__title">ChocoMesseの3大特徴</h2>
            <p className="lp-section__subtitle">あなたの大切なプライベートと秘密を守る仕組み</p>

            <div className="lp-features">
              <div className="lp-feature-card">
                <div className="lp-feature-card__badge" aria-hidden="true">1</div>
                <div className="lp-feature-card__info">
                  <h3 className="lp-feature-card__title">登録不要・完全匿名で使える</h3>
                  <p className="lp-feature-card__text">
                    メールアドレス、電話番号、SNS連携などの登録作業は一切ありません。ニックネームだけで始められるため、個人情報が漏洩するリスクが完全にゼロです。
                  </p>
                </div>
              </div>

              <div className="lp-feature-card">
                <div className="lp-feature-card__badge" aria-hidden="true">2</div>
                <div className="lp-feature-card__info">
                  <h3 className="lp-feature-card__title">絶対に割り込めない二人専用スペース</h3>
                  <p className="lp-feature-card__text">
                    同じ合言葉であっても、ルームには最大2人までしか入室できません。3人目が割り込んで会話を盗み見たり、グループに追加されたりする心配がない「完全な密室」です。
                  </p>
                </div>
              </div>

              <div className="lp-feature-card">
                <div className="lp-feature-card__badge" aria-hidden="true">3</div>
                <div className="lp-feature-card__info">
                  <h3 className="lp-feature-card__title">合言葉（パスワード）はサーバーに送られません</h3>
                  <p className="lp-feature-card__text">
                    設定した合言葉は、ブラウザ側で高度に暗号化（ハッシュ化）されてから送信されます。サーバーのデータベースにも平文で保存されないため、安全性が極めて高い設計です。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ホーム画面アイコンのカモフラージュ */}
          <section className="lp-section lp-section--light" aria-label="ホーム画面アイコンのカモフラージュ">
            <div className="lp-section-inner">
              <h2 className="lp-section__title">ホーム画面に置いてもバレにくい</h2>
              <p className="lp-section__subtitle">アイコン設定で、アプリの見た目を自由に変えられます</p>

              <div className="lp-troubles">
                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">🏠</span>
                  <h3 className="lp-trouble-card__title">ホームに置くアイコンを選べる</h3>
                  <p className="lp-trouble-card__text">
                    メニューの「アイコン設定」から、「ホーム画面に追加」したときに表示されるアイコンを8種類の中から自由に選べます。
                  </p>
                </div>

                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">🎭</span>
                  <h3 className="lp-trouble-card__title">チャットと気づかれにくいデザインも</h3>
                  <p className="lp-trouble-card__text">
                    一見チャットアプリとは気づかれないアイコンも用意。スマホのホーム画面を見られても、ヒミツのチャットだと悟られにくくなります。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 使い方ステップ */}
          <section id="how-it-works" className="lp-section lp-section--light" aria-label="使い方ステップ">
            <div className="lp-section-inner">
              <h2 className="lp-section__title">かんたん3ステップ</h2>
              <p className="lp-section__subtitle">誰でも今すぐ10秒でチャットを開始できます</p>

              <div className="lp-steps">
                <div className="lp-step-card">
                  <span className="lp-step-card__num" aria-hidden="true">1</span>
                  <span className="lp-step-card__icon" aria-hidden="true">🔑</span>
                  <h3 className="lp-step-card__title">合言葉を決める</h3>
                  <p className="lp-step-card__text">
                    二人だけの合言葉（パスワード）とニックネームを決めて入力します。
                  </p>
                </div>

                <div className="lp-step-card">
                  <span className="lp-step-card__num" aria-hidden="true">2</span>
                  <span className="lp-step-card__icon" aria-hidden="true">🗣️</span>
                  <h3 className="lp-step-card__title">相手に共有する</h3>
                  <p className="lp-step-card__text">
                    相手にも同じ「合言葉」を教えます（ニックネームは何でもOKです）。
                  </p>
                </div>

                <div className="lp-step-card">
                  <span className="lp-step-card__num" aria-hidden="true">3</span>
                  <span className="lp-step-card__icon" aria-hidden="true">💖</span>
                  <h3 className="lp-step-card__title">チャット開始！</h3>
                  <p className="lp-step-card__text">
                    相手が同じ合言葉で入室すれば、リアルタイムチャットが始まります。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* よくある質問 */}
          <section className="lp-section" aria-label="よくある質問">
            <h2 className="lp-section__title">よくある質問</h2>
            <p className="lp-section__subtitle">ご利用にあたって気になる疑問にお答えします</p>

            <div className="lp-faqs">
              {faqData.map((faq, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div
                    key={index}
                    className={isOpen ? 'lp-faq-item lp-faq-item--open' : 'lp-faq-item'}
                  >
                    <div
                      className="lp-faq-item__q"
                      onClick={() => toggleFaq(index)}
                      role="button"
                      aria-expanded={isOpen}
                    >
                      {faq.q}
                    </div>
                    <div className="lp-faq-item__a">
                      {faq.a}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* アカウント登録でできること */}
          <section className="lp-section lp-section--light" aria-label="アカウント登録でできること">
            <div className="lp-section-inner">
              <h2 className="lp-section__title">アカウント登録でもっと便利に</h2>
              <p className="lp-section__subtitle">登録は無料。あなたの部屋をかんたんに管理できます</p>

              <div className="lp-troubles">
                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">📋</span>
                  <h3 className="lp-trouble-card__title">部屋をまとめて管理</h3>
                  <p className="lp-trouble-card__text">
                    作成した部屋を保存して、専用ダッシュボードからいつでも再入室。各部屋の未読件数もひと目で確認できます。
                  </p>
                </div>

                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">👀</span>
                  <h3 className="lp-trouble-card__title">既読をコントロール</h3>
                  <p className="lp-trouble-card__text">
                    既読の表示・非表示を部屋ごとに切り替え。相手が読んだかどうかや、未読メッセージ数を管理できます。
                  </p>
                </div>

                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">✋</span>
                  <h3 className="lp-trouble-card__title">メッセージ数を制限</h3>
                  <p className="lp-trouble-card__text">
                    相手（ゲスト）の連続送信数に上限を設定して、会話のペースを自分でコントロールできます。
                  </p>
                </div>

                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">🚫</span>
                  <h3 className="lp-trouble-card__title">NGワードを設定</h3>
                  <p className="lp-trouble-card__text">
                    見せたくない・見たくない言葉を登録しておくと、該当するメッセージを自動でブロックします。
                  </p>
                </div>

                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">⚡</span>
                  <h3 className="lp-trouble-card__title">定型文をワンタップ送信</h3>
                  <p className="lp-trouble-card__text">
                    よく使うメッセージをテンプレートとして登録。タップするだけで素早く返信できます。
                  </p>
                </div>

                <div className="lp-trouble-card">
                  <span className="lp-trouble-card__icon" aria-hidden="true">📌</span>
                  <h3 className="lp-trouble-card__title">まとめて一括設定</h3>
                  <p className="lp-trouble-card__text">
                    複数の部屋に、お知らせ（アナウンス）やメッセージ制限をまとめて適用できます。
                  </p>
                </div>
              </div>

              <div className="lp-benefit-cta">
                <button
                  type="button"
                  className="lp-cta-button"
                  onClick={props.onGoToLogin}
                >
                  無料でアカウント登録する →
                </button>
              </div>
            </div>
          </section>

          {/* ベータ版注意 */}
          <div className="lp-beta-notice" role="note">
            <span aria-hidden="true">🚧</span>
            現在ベータ版のため、予期しない不具合が発生する場合があります。また、正式版リリース時に一部機能がプレミアム（有料）機能へ移行する場合があります。
          </div>

          {/* フッター */}
          <footer className="lp-footer">
            <p>&copy; {new Date().getFullYear()} Ashiken AI Labs. All Rights Reserved.</p>
          </footer>
        </div>
      </div>
    </div>
  );
}
